import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const JobFilterContext = createContext();
const convertToNumbers = (arr) => arr.map((val) => Number(val));

export const JobFilterProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filtersInitialValues = useMemo(
    () => ({
      functionValue: convertToNumbers(
        searchParams.get('functionValue') ? searchParams.get('functionValue').split(',') : []
      ),
      industries: convertToNumbers(searchParams.get('industries') ? searchParams.get('industries').split(',') : []),
      opportunityType: searchParams.get('opportunityType') ? searchParams.get('opportunityType').split(',') : [],
      minSalary: searchParams.get('minSalary') || '',
      maxSalary: searchParams.get('maxSalary') || '',
      workType: searchParams.get('workType') ? searchParams.get('workType').split(',') : [],
      experienceLevel: convertToNumbers(
        searchParams.get('experienceLevel') ? searchParams.get('experienceLevel').split(',') : []
      ),
      skills: convertToNumbers(searchParams.get('skills') ? searchParams.get('skills').split(',') : []),
      requiredEducation: convertToNumbers(
        searchParams.get('requiredEducation') ? searchParams.get('requiredEducation').split(',') : []
      ),
      remoteWork: convertToNumbers(searchParams.get('remoteWork') ? searchParams.get('remoteWork').split(',') : []),
      alert: false,
    }),
    [searchParams]
  );
  const [jobFilterValues, setJobFilterValues] = useState(filtersInitialValues);

  useEffect(() => {
    if (!searchParams.size) {
      setJobFilterValues(filtersInitialValues);
    }
  }, [searchParams]);

  const initialFilters = {
    industries: jobFilterValues.industries,
    function: jobFilterValues.functionValue,
    min_salary: jobFilterValues.minSalary,
    max_salary: jobFilterValues.maxSalary,
    work_types: jobFilterValues.opportunityType,
    experience: jobFilterValues.experienceLevel,
    education: jobFilterValues.requiredEducation,
    remote: jobFilterValues.workType,
    urgency: jobFilterValues.remoteWork,
    skills: jobFilterValues.skills,
  };

  const updateSearchParams = (key, value) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        searchParams.set(key, value.join(','));
      } else {
        searchParams.delete(key);
      }
    } else if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }

    setSearchParams(searchParams);
  };

  const updateJobFilterValues = (key, value) => {
    setJobFilterValues((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [key]: value,
      };
      updateSearchParams(key, value);
      return newFilters;
    });
  };

  const getTotalSelectedCount = (values = jobFilterValues) => {
    let count = 0;

    if (values.functionValue && values.functionValue.length > 0) {
      count += values.functionValue.length;
    }
    if (values.industries && values.industries.length > 0) {
      count += values.industries.length;
    }
    if (values.opportunityType && values.opportunityType.length > 0) {
      count += values.opportunityType.length;
    }
    if (values.workType && values.workType.length > 0) {
      count += values.workType.length;
    }
    if (values.experienceLevel && values.experienceLevel.length > 0) {
      count += values.experienceLevel.length;
    }
    if (values.skills && values.skills.length > 0) {
      count += values.skills.length;
    }
    if (values.requiredEducation && values.requiredEducation.length > 0) {
      count += values.requiredEducation.length;
    }
    if (values.remoteWork && values.remoteWork.length > 0) {
      count += values.remoteWork.length;
    }
    if (values.minSalary && values.minSalary.trim() !== '') {
      count += 1;
    }
    if (values.maxSalary && values.maxSalary.trim() !== '') {
      count += 1;
    }

    return count;
  };

  const contextValue = useMemo(
    () => ({
      jobFilterValues,
      setJobFilterValues,
      updateJobFilterValues,
      updateSearchParams,
      initialFilters,
      getTotalSelectedCount,
    }),
    [jobFilterValues]
  );

  return <JobFilterContext.Provider value={contextValue}>{children}</JobFilterContext.Provider>;
};

export const useJobFilterContext = () => useContext(JobFilterContext);
